import { useLocation } from "react-router";

/**
 * Attorney Hub's public library, which is its own place rather than a view of the reader's.
 *
 * It has a separate address because it is a separate knowledge base: everyone can read it, nobody
 * outside Attorney Hub curates it, and it does not belong to the organization the reader signed in
 * as. `attorney-hub` is a reserved slug on the server, so no article can ever be created that would
 * shadow this address.
 */
export const attorneyHubKnowledgeBaseRoot = "/knowledge-base/attorney-hub";

/**
 * The knowledge base of whichever organization the reader is looking at — their own, or a client's
 * external rules for the work they do. Which one is in the query string, not the path.
 */
export const entityKnowledgeBaseRoot = "/knowledge-base";

/**
 * Which knowledge base an address is inside.
 *
 * Every link within the contents has to be built from this rather than from a fixed prefix.
 * Hard-coding `/knowledge-base/` is what made opening an Attorney Hub article land the reader in
 * their own organization's knowledge base instead: the article was fine, the address it was linked
 * at belonged to a different library.
 */
export function knowledgeBaseRootIn(pathname: string): string {
  return pathname === attorneyHubKnowledgeBaseRoot
    || pathname.startsWith(`${attorneyHubKnowledgeBaseRoot}/`)
    ? attorneyHubKnowledgeBaseRoot
    : entityKnowledgeBaseRoot;
}

export function articlePathIn(root: string, slug: string, search = ""): string {
  return `${root}/${slug}${search}`;
}

export function articleEditorPathIn(root: string, slug: string, search = ""): string {
  return `${root}/${slug}/edit${search}`;
}

/**
 * The query string that says whose knowledge base is open, and which of their two it is.
 *
 * One definition, because every address that names someone else's library has to agree with what
 * `KnowledgeBasePage` reads back out of the address bar. Two spellings of this would fail silently:
 * the page would simply fall back to the reader's own library and look like a permissions problem.
 */
function whoseLibraryQuery(library: string, ownerLegalEntityId: string): string {
  return `?${new URLSearchParams({ library, owner: ownerLegalEntityId }).toString()}`;
}

/**
 * A particular organization's external library — their rules for the work you do for them.
 *
 * Built here so that anything outside the knowledge base can link to a client's rules without
 * knowing how "whose library" is encoded. External and not a choice of library: the only one of
 * another organization's that an outsider ever reads is the external one.
 */
export function clientLibraryPath(ownerLegalEntityId: string): string {
  return `${entityKnowledgeBaseRoot}${whoseLibraryQuery("External", ownerLegalEntityId)}`;
}

/**
 * Where a search hit is read, which is not necessarily where it was searched from.
 *
 * Search crosses every library the reader may see, so a hit can belong to Attorney Hub's library,
 * to the reader's own, or to a client whose external rules they can open. The link has to name that
 * library, or opening the hit asks the reader's current library for an article that is not in it.
 */
export function searchResultPathIn(
  library: string,
  ownerLegalEntityId: string,
  slug: string
): string {
  if (library === "AttorneyHubPublic") {
    return articlePathIn(attorneyHubKnowledgeBaseRoot, slug);
  }

  return articlePathIn(
    entityKnowledgeBaseRoot,
    slug,
    whoseLibraryQuery(library, ownerLegalEntityId)
  );
}

/**
 * Addresses within the knowledge base the reader is currently in.
 *
 * The query string is carried along because it is what says *whose* knowledge base is open on the
 * entity route — `?library=External&owner=…` for a client's rules. Dropping it on a link would
 * silently return the reader to their own library the moment they opened an article.
 */
export function useKnowledgeBaseLocations() {
  const { pathname, search } = useLocation();
  const root = knowledgeBaseRootIn(pathname);

  return {
    root,
    articlePath: (slug: string) => articlePathIn(root, slug, search),
    articleEditorPath: (slug: string) => articleEditorPathIn(root, slug, search),
  };
}
